import { getParent, types, type Instance } from 'mobx-state-tree';
import type { IInspectorParent } from './inspector_parent';
import { isEqual } from 'lodash';

/** The change keys stored by the transition inspector. */
type TransitionChangeKey = 'metaMethodIds';

/**
 * Store the changes in the state inspector that are currently pending.
 */
const TransitionInspectorChanges = types
  .model('TransitionInspectorChanges', {
    metaMethodIds: types.optional(types.maybeNull(types.array(types.string)), null),
  })
  .views((self) => ({
    // the inspector is dirty if any of the attributes has changes
    get dirty() {
      return self.metaMethodIds !== null;
    },
    isDirty(key: TransitionChangeKey) {
      return self[key] !== null;
    },
  }))
  .actions((self) => {
    function metaMethodIdsEqual(original: string[], change: string[]) {
      return isEqual(original.slice().sort(), change.slice().sort());
    }

    return {
      /**
       * Store a change for the given key.
       *
       * This action also receives the original value of the attribute and clears
       * the stored change if the new value for the change is the same as the
       * original value.
       */
      storeChange(key: TransitionChangeKey, original: string[], change: string[]) {
        if (
          (key === 'metaMethodIds' && !metaMethodIdsEqual(original, change)) ||
          (key !== 'metaMethodIds' && original !== change)
        ) {
          self[key] = change as unknown as typeof self.metaMethodIds;
        } else {
          self[key] = null;
        }
      },
      discard() {
        self.metaMethodIds = null;
      },
    };
  });

/**
 * UI state for the state inspector
 */
const TransitionInspector = types
  .model('TransitionInspector', {
    // the pending changes
    changes: types.optional(TransitionInspectorChanges, () =>
      TransitionInspectorChanges.create({})
    ),
  })
  .views((self) => ({
    // We are dirty when we have changes
    get dirty() {
      return self.changes.dirty;
    },
  }))
  .actions((self) => ({
    // Discard our changes
    discardChanges() {
      self.changes.discard();
    },
    setProjectBusy(busy: boolean) {
      getParent<IInspectorParent>(self).setProjectBusy(busy);
    },
  }));

export type ITransitionInspector = Instance<typeof TransitionInspector>;

export default TransitionInspector;
